/**
 * Context-Aware Suggestions Component
 * Provides smart query suggestions based on current route/page context
 */

import React, { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import './ContextAwareSuggestions.css';

interface ContextAwareSuggestionsProps {
  onSelect: (query: string) => void;
}

export const ContextAwareSuggestions: React.FC<
  ContextAwareSuggestionsProps
> = ({ onSelect }) => {
  const location = useLocation();
  const params = useParams();

  const suggestions = useMemo(() => {
    const path = location.pathname;

    // Lead detail page
    if (path.match(/\/leads\/\d+/)) {
      const leadId = params.id;
      return [
        {
          icon: '📊',
          text: 'Statistika ovog lead-a',
          query: `Daj mi detalje i statistiku za lead ${leadId}`,
        },
        {
          icon: '📝',
          text: 'Dodaj komentar',
          query: `Dodaj komentar na lead ${leadId}`,
        },
        {
          icon: '🔄',
          text: 'Promeni status',
          query: `Promeni status lead-a ${leadId}`,
        },
        {
          icon: '📧',
          text: 'Pošalji email',
          query: `Pripremi email template za lead ${leadId}`,
        },
      ];
    }

    // Ticket detail page
    if (path.match(/\/tickets\/\d+/)) {
      const ticketId = params.id;
      return [
        {
          icon: '🎫',
          text: 'Detalji tiketa',
          query: `Daj mi detalje tiketa ${ticketId}`,
        },
        {
          icon: '👤',
          text: 'Dodeli tiket',
          query: `Dodeli tiket ${ticketId} meni`,
        },
        {
          icon: '⚡',
          text: 'Promeni prioritet',
          query: `Postavi visok prioritet na tiket ${ticketId}`,
        },
        {
          icon: '💬',
          text: 'Dodaj komentar',
          query: `Dodaj komentar na tiket ${ticketId}`,
        },
      ];
    }

    // Kanban board page
    if (path.match(/\/kanban\/[\w-]+/)) {
      const boardSlug = params.slug || 'development';
      return [
        {
          icon: '📋',
          text: 'Statistika board-a',
          query: `Statistika za board '${boardSlug}'`,
        },
        {
          icon: '➕',
          text: 'Dodaj tiket',
          query: `Kreiraj novi tiket na board-u '${boardSlug}'`,
        },
        {
          icon: '🔍',
          text: 'Urgent items',
          query: `Koji su urgentni taskovi na board-u '${boardSlug}'?`,
        },
        {
          icon: '📊',
          text: 'Moj workload',
          query: `Moj workload na board-u '${boardSlug}'`,
        },
      ];
    }

    // Dashboard / Home
    if (path === '/' || path === '/dashboard') {
      return [
        {
          icon: '📊',
          text: 'Ukupna statistika',
          query: 'Koliko imamo aktivnih tiketa i lead-ova?',
        },
        {
          icon: '⚡',
          text: 'Moji urgent taskovi',
          query: 'Koji su moji urgentni taskovi?',
        },
        {
          icon: '📅',
          text: 'Moj workload',
          query: 'Moj workload ovaj mesec',
        },
        {
          icon: '🎯',
          text: 'High priority tiketi',
          query: 'Pokaži mi high priority tikete',
        },
      ];
    }

    // Leads list page
    if (path.match(/\/leads\/?$/)) {
      return [
        {
          icon: '🆕',
          text: 'Nedavni lead-ovi',
          query: 'Pokaži mi nedavne lead-ove',
        },
        {
          icon: '📊',
          text: 'Koliko imam lead-ova',
          query: 'Koliko imam aktivnih lead-ova?',
        },
        {
          icon: '🔍',
          text: 'Pretraži lead-ove',
          query: "Pronađi lead-ove sa 'Digital' u nazivu",
        },
        {
          icon: '➕',
          text: 'Kreiraj lead',
          query: 'Kreiraj novi lead',
        },
      ];
    }

    // Tickets list page
    if (path.match(/\/tickets\/?$/)) {
      return [
        {
          icon: '📋',
          text: 'Moji tiketi',
          query: 'Koliko imam otvorenih tiketa?',
        },
        {
          icon: '⚡',
          text: 'High priority',
          query: 'Pokaži mi high priority tikete',
        },
        {
          icon: '🆕',
          text: 'Kreiraj tiket',
          query: 'Kreiraj tiket za bug u login sistemu',
        },
        {
          icon: '🔍',
          text: 'Pretraži tikete',
          query: 'Pronađi tikete sa statusom "open"',
        },
      ];
    }

    // Calendar page
    if (path.match(/\/calendar/)) {
      return [
        {
          icon: '📅',
          text: 'Moji eventi danas',
          query: 'Koji su moji eventi danas?',
        },
        {
          icon: '📆',
          text: 'Eventi ove nedelje',
          query: 'Pokaži mi sve eventi ove nedelje',
        },
        {
          icon: '➕',
          text: 'Dodaj event',
          query: 'Kreiraj novi event',
        },
        {
          icon: '⏰',
          text: 'Nadolazeći sastanci',
          query: 'Koji su moji nadolazeći sastanci?',
        },
      ];
    }

    // Default suggestions (no specific context)
    return [
      {
        icon: '🔍',
        text: 'Pretraži',
        query: 'Pretraži tikete i lead-ove',
      },
      {
        icon: '📊',
        text: 'Statistika',
        query: 'Koliko imamo aktivnih tiketa i lead-ova?',
      },
      {
        icon: '⚡',
        text: 'Urgent items',
        query: 'Koji su moji urgentni taskovi?',
      },
      {
        icon: '📋',
        text: 'Kanban boards',
        query: 'Koji board ima najviše tiketa?',
      },
    ];
  }, [location.pathname, params.id, params.slug]);

  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return (
    <div className="context-suggestions">
      <div className="context-suggestions-label">
        💡 Sugestije za ovu stranicu:
      </div>
      <div className="suggestions-list">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            className="suggestion-chip"
            onClick={() => onSelect(suggestion.query)}
            title={suggestion.query}
          >
            <span className="suggestion-icon">{suggestion.icon}</span>
            <span className="suggestion-text">{suggestion.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
